import moment from "moment"
import Fuse from "fuse.js"

import { timeBetween, fuseConfig } from "./sharedHelperFunctions"

/*
These should all be pure functions that take in data and return data without mutating anything.
*/

const compareByAssetName = (resultA, resultB, ifEqual) => {
  const a = resultA.item.name.toLowerCase()
  const b = resultB.item.name.toLowerCase()
  return a > b ? 1 : a < b ? -1 : ifEqual(resultA, resultB)
}

const compareByLocationName = (resultA, resultB, ifEqual) => {
  if (!resultA.item.location || !resultB.item.location) {
    return ifEqual(resultA, resultB)
  }
  const a = resultA.item.location.name.toLowerCase()
  const b = resultB.item.location.name.toLowerCase()
  return a > b ? 1 : a < b ? -1 : ifEqual(resultA, resultB)
}

const compareByPatientArea = (resultA, resultB, ifEqual) => {
  if (!resultA.item.location || !resultB.item.location) {
    return ifEqual(resultA, resultB)
  }
  const a = !resultA.item.location.patientArea
  const b = !resultB.item.location.patientArea
  return a < b ? 1 : a > b ? -1 : ifEqual(resultA, resultB)
}

const compareByScore = (resultA, resultB, ifEqual) => {
  if (resultA.score === resultB.score) {
    return ifEqual(resultA, resultB)
  } else {
    return resultA.score - resultB.score
  }
}

const compareBySeen = (resultA, resultB, ifEqual) => {
  const a = !!resultA.item.location
  const b = !!resultB.item.location
  return a < b ? 1 : a > b ? -1 : ifEqual(resultA, resultB)
}

const compareByMissing = (resultA, resultB, ifEqual) => {
  const a = resultA.isMissing
  const b = resultB.isMissing
  return a > b ? 1 : a < b ? -1 : ifEqual(resultA, resultB)
}

/*
 * These nested functions determine the order of the sort
 * Return if not equal, otherwise continue the chain
 * See: https://en.wikipedia.org/wiki/Continuation-passing_style
 */
const customCompare = (resultA, resultB) => {
  return compareBySeen(resultA, resultB, (a, b) =>
    compareByMissing(a, b, (a, b) =>
      compareByScore(a, b, (a, b) =>
        compareByAssetName(a, b, (a, b) =>
          compareByPatientArea(a, b, (a, b) =>
            compareByLocationName(a, b, () => 0)
          )
        )
      )
    )
  )
}

const getResultWithTimeCalculations = result => {
  const lastSeenAt = Date.parse(result.item.lastSeenAt)
  const lastMovedAt = Date.parse(result.item.lastMovedAt)

  const timeStationary = timeBetween(lastMovedAt, lastSeenAt)
  const timeSinceDetected = timeBetween(lastSeenAt, Date.now())

  return {
    ...result,
    timeStationary: timeStationary,
    isStationary: timeStationary > 300000,
    timeSinceDetected: timeSinceDetected,
    isMissing: timeSinceDetected > 3600000,
  }
}

const searchBoxResults = (results, searchTerms) => {
  let searchResults
  if (Object.values(searchTerms).filter(v => v !== "").length > 0) {
    searchResults = filterResults(results, searchTerms)
  } else {
    // The case where all search inputs are empty. We don't need to run a search, but we need to
    // keep the result format the same as when searching
    searchResults = results.map(r => ({ item: r, score: -1 }))
  }
  return searchResults
}

const filterByOwner = (results, departmentId) => {
  return results.filter(result =>
    result.owners.map(o => o.id).includes(departmentId)
  )
}

const filterByLocation = (results, unitId) => {
  return results.filter(
    result => result.location && result.location.unit.id === unitId
  )
}

const filterByKeywords = (results, searchQuery) => {
  let params = {
    ...fuseConfig,
    keys: [
      "name",
      "assetId",
      "serialNumber",
      "labels",
      "manufacturer",
      "modelName",
    ],
  }
  if (searchQuery[0] === '"' && searchQuery[searchQuery.length - 1] === '"') {
    // replaces fuzzy searching for an exact match on any field when the input is surrounded by double quotes
    // updates the parameters to take advantage of Unix-style matching (https://fusejs.io/examples.html#extended-search)
    // and updates the search query to find the exact match
    // within Fuse, ^searchterm returns any result beginning with searchterm
    // searchterm$ looks returns any result ending with searchterm
    // and an empty space is equivalent to an AND condition, meaning that
    // ^searchterm searchterm$ returns any result starting AND ending with searchterm i.e., an exact match.
    params = {
      ...params,
      useExtendedSearch: true,
    }
    const searchTerm = searchQuery.slice(1, searchQuery.length - 1)
    searchQuery = `^${searchTerm} ${searchTerm}$`
  }
  const fuse = new Fuse(results, params)
  return fuse.search(searchQuery)
}

const filterResults = (results, searchStore) => {
  if (searchStore.unitId && searchStore.unitId.length > 0) {
    results = filterByLocation(results, searchStore.unitId)
  }
  if (searchStore.departmentId && searchStore.departmentId.length > 0) {
    results = filterByOwner(results, searchStore.departmentId)
  }
  if (
    searchStore.keywords &&
    searchStore.keywords.length > 0 &&
    searchStore.keywords.trim() !== ""
  ) {
    results = filterByKeywords(results, searchStore.keywords)
  } else {
    // This keeps the result format the same as searching
    results = results.map(r => ({ item: r, score: -1 }))
  }
  return results
}

export const latestUpdatedTime = allAssets => {
  const maxLastSeenAt = Math.max(
    ...allAssets.filter(r => r.lastSeenAt).map(r => Date.parse(r.lastSeenAt))
  )

  return moment.duration(Date.now() - maxLastSeenAt).humanize()
}

export const getSearchedAssetsWithTimeCalculations = (allAssets, searchTerms) =>
  searchBoxResults(allAssets, searchTerms)
    .map(getResultWithTimeCalculations)
    .sort(customCompare)
